import { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { arrayMoveImmutable } from 'utils/arrayMove';
import styled from 'styled-components';
import { FormField } from 'ui/formField';
import { GhostSuccessButton } from 'ui/button';
import { colors, link2 } from 'ui/theme';
import { Add16, NegativeFill } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import {
  constructFieldErrors,
  FieldNames,
  SettingsReadOnlyText,
  TemplateEditViews,
  ValidationError,
  GqlUpsertTemplateInvalidContentFailureTypes,
} from '../../common';
import {
  CallButton,
  UrlButton,
  WhatsAppButtonDefinition,
  WhatsAppContentDefinition,
} from '../../../graphql/types';
import { getButtonCounts, selectDefaultButton } from '../utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { ButtonTypeVariations } from './ButtonTypeVariations';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { useEditableWhatsApp } from '../hooks/useEditableWhatsApp';

const StyledReadOnlyText = styled(SettingsReadOnlyText)`
  display: flex;
  flex-wrap: wrap;
`;

const StyledAddIcon = styled(Add16)`
  margin-right: ${({ theme }) => theme.spacing.threeQuarter};
`;

export const ButtonTitle = styled.span<{ $hasError: boolean; $isLink: boolean }>`
  ${({ $isLink }) => $isLink && link2}
  color: ${({ theme, $hasError, $isLink }) => {
    if ($hasError) {
      return theme.colors.utilityColors.negative100;
    } else if ($isLink) {
      return theme.colors.textColors.textLink;
    } else {
      return theme.colors.textColors.textPrimary;
    }
  }};
  text-decoration-color: ${({ theme, $hasError, $isLink }) => {
    if ($hasError) return theme.colors.utilityColors.negative100;
    else if ($isLink) return theme.colors.textColors.textLink;
    else return theme.colors.textColors.textPrimary;
  }};
  padding-right: 15px;
`;

const MAX_NUMBER_OF_BUTTONS = 10;

type Props = { isReviewTemplateTakeover?: boolean };

export const Buttons = ({ isReviewTemplateTakeover = false }: Props) => {
  const { view } = useQueryParamContext();
  const {
    isEditMode: isEditModeContext,
    liveTemplate,
    setCurrentMediumField,
  } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const { isEditableWhatsAppTemplate } = useEditableWhatsApp();

  const whatsAppData = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const buttonList = (whatsAppData.buttons || []) as WhatsAppButtonDefinition[];
  const [errorArr, setErrorArr] = useState([] as any[]);
  const isEditMode = view === TemplateEditViews.preview || isEditModeContext;
  const isEditable = isEditMode && isEditableWhatsAppTemplate;

  useEffect(() => {
    if (localeContentErrors) {
      const newArr = localeContentErrors.reduce((acc: any[], currentValue: ValidationError) => {
        const tempArr: any[] = [...acc];
        if (currentValue.fieldName === FieldNames.BUTTONS && currentValue.index !== undefined) {
          if (!tempArr[currentValue.index]) {
            tempArr[currentValue.index] = {
              buttonTextErrors: [],
              buttonUrlErrors: [],
              buttonPhoneErrors: [],
            };
          }

          if (
            [
              GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_PARAMS,
              GqlUpsertTemplateInvalidContentFailureTypes.INVALID_WHATSAPP_BUTTON_URL_FORMAT,
              GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_URL,
            ].includes(currentValue?.__typename)
          ) {
            tempArr[currentValue.index].buttonUrlErrors.push(
              currentValue?.subMessage || currentValue?.errorMessage,
            );
          } else if (
            [
              GqlUpsertTemplateInvalidContentFailureTypes.INVALID_SNIPPET_USAGE,
              GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COMPLEXITY,
              GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE_WITH_INDEX,
              GqlUpsertTemplateInvalidContentFailureTypes.INVALID_HANDLEBAR_COUNT,
              GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_TEXT,
              GqlUpsertTemplateInvalidContentFailureTypes.EMOJI_IN_BUTTON_TEXT,
            ].includes(currentValue?.__typename)
          ) {
            tempArr[currentValue.index].buttonTextErrors.push(
              currentValue?.subMessage || currentValue?.errorMessage,
            );
          } else if (
            [
              GqlUpsertTemplateInvalidContentFailureTypes.EMPTY_WHATSAPP_BUTTON_PHONE_NUMBER,
            ].includes(currentValue?.__typename)
          ) {
            tempArr[currentValue.index].buttonPhoneErrors.push(currentValue?.errorMessage);
          }
        }
        return tempArr;
      }, []);
      setErrorArr(newArr);
    }
  }, [localeContentErrors]);

  const setButtonList = (list: WhatsAppButtonDefinition[]) =>
    setCurrentMediumField('buttons', list);

  const buttonCounts = getButtonCounts(buttonList);

  const handleAddParam = () => {
    // @ts-ignore
    setButtonList([...buttonList, selectDefaultButton(buttonCounts)]);
  };

  const sortButtonList = useCallback(
    (dragEndObj: any) => {
      const sourceIndex = dragEndObj.source.index;
      const destinationIndex = dragEndObj.destination?.index;

      if (typeof sourceIndex === 'number' && typeof destinationIndex === 'number') {
        const newArrayList = arrayMoveImmutable(buttonList, sourceIndex, destinationIndex);
        const newErrorArrList = arrayMoveImmutable(errorArr, sourceIndex, destinationIndex);
        setButtonList(newArrayList);
        setErrorArr(newErrorArrList);
      }
    },
    [buttonList, errorArr],
  );

  const getButtonTitles = (index: number, title?: string) => {
    return index + 1 !== buttonList.length ? `${title},` : title;
  };

  const { hasError: hasButtonError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.BUTTONS,
  });

  const readOnlyText = SETTINGS_DISPLAY_CONTENT.unsetText;

  if (isReviewTemplateTakeover && !whatsAppData.buttons?.length) return null;

  return (
    <FormField
      data-test="whats-app-buttons-form-field"
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.buttons.labelText}
    >
      {isEditable ? (
        <>
          <DragDropContext onDragEnd={sortButtonList}>
            <Droppable droppableId="buttons-droppable">
              {(droppableProvided) => (
                <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                  {buttonList.map((value: WhatsAppButtonDefinition, index: number) => (
                    <Draggable key={`item-${index}`} draggableId={`item-${index}`} index={index}>
                      {(draggableProvided) => (
                        <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                          <ButtonTypeVariations
                            index={index}
                            value={value}
                            draggableProvided={draggableProvided}
                            buttonCounts={buttonCounts}
                            errorArr={errorArr}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <Tooltip
            isEnabled={buttonList.length === MAX_NUMBER_OF_BUTTONS}
            tooltipContent="Max of 10 buttons"
          >
            <GhostSuccessButton
              onClick={handleAddParam}
              isDisabled={buttonList.length === MAX_NUMBER_OF_BUTTONS}
            >
              <StyledAddIcon />
              {SETTINGS_DISPLAY_CONTENT.buttons.buttonText}
            </GhostSuccessButton>
          </Tooltip>
        </>
      ) : (
        <StyledReadOnlyText
          isUnset={whatsAppData?.buttons?.length === 0}
          $hasError={hasButtonError}
          data-test="button-ro"
        >
          {whatsAppData.buttons && whatsAppData.buttons.length > 0 ? (
            <>
              {whatsAppData.buttons.map((button: WhatsAppButtonDefinition, i) => {
                const isLinkButton =
                  typeof (button as UrlButton)?.url === 'string' ||
                  typeof (button as CallButton)?.phoneNumber === 'string';
                return (
                  <>
                    {!!errorArr[i] && <NegativeFill color={colors.red100} />}
                    <ButtonTitle key={i} $hasError={!!errorArr[i]} $isLink={isLinkButton}>
                      {getButtonTitles(i, button.title)}
                    </ButtonTitle>
                  </>
                );
              })}
            </>
          ) : (
            readOnlyText
          )}
        </StyledReadOnlyText>
      )}
    </FormField>
  );
};
